import React, { useState } from 'react';
import { useTheme, alpha, Tabs, Tab, Box, Button, useMediaQuery } from '@mui/material';
import { typography } from 'theme';

/**
 * CustomTabs Component
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects with { label, content, show }
 * @param {string} props.variant - 'tabs' or 'buttons' to render as tabs or buttons
 * @param {number} props.defaultTab - Default active tab index (default: 0)
 * @param {Function} props.onTabChange - Callback function called when tab changes
 * @param {Object} props.tabsProps - Additional props for MUI Tabs component
 * @param {Object} props.tabProps - Additional props for MUI Tab component
 * @param {Object} props.buttonProps - Additional props for MUI Button component
 */
const CustomTabs = ({
  tabs = [],
  variant = 'tabs',
  defaultTab = 0,
  onTabChange,
  tabsProps = {},
  tabProps = {},
  buttonProps = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Filter tabs based on 'show' property (default to true if not specified)
  const visibleTabs = tabs.filter((tab) => tab.show !== false);

  const [activeTab, setActiveTab] = useState(defaultTab);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  const handleButtonClick = (index) => {
    setActiveTab(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  if (visibleTabs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' ,}}>
      {/* Render as MUI Tabs */}
      {variant === 'tabs' && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            {...tabsProps}
            sx={{
              backgroundColor: '#13172A',
            }}

          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                {...tabProps}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Render as Buttons */}
      {variant === 'buttons' && (
        <Box
          sx={{
            display: 'flex',
            backgroundColor: alpha(theme.palette.common.white, 0.04),
            borderRadius: theme.shape.borderRadius,
            padding: { xs: theme.spacing(0.25), sm: theme.spacing(0.5) },
            marginBottom: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
            gap: theme.spacing(0.5),
            flexDirection: 'row',
            ...tabsProps?.sx,
          }}
        >
          {visibleTabs.map((tab, index) => {
            const isActive = activeTab === index;
            const baseButtonSx = {
              flex: 1,
              backgroundColor: isActive 
                ? { xs: theme.palette.primary.main, sm: 'transparent' }
                : 'transparent',
              color: isActive 
                ? (theme.palette.custom?.text?.heading || theme.palette.common.white)
                : (theme.palette.custom?.text?.helperDisabled || theme.palette.grey[500]),
              border: isActive 
                ? `2px solid ${theme.palette.primary.main}` 
                : 'none',
              borderRadius: theme.shape.borderRadius,
              padding: { xs: theme.spacing(0.75), sm: theme.spacing(1)},
              minHeight: 'auto',
              fontSize: { xs: typography.fontSize.helperText, sm: typography.fontSize.label },
              fontWeight: typography.fontWeight.medium,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.05),
              },
            };
            
            // Merge with buttonProps.sx, allowing function for conditional styling
            let mergedSx = baseButtonSx;
            if (buttonProps?.sx) {
              if (typeof buttonProps.sx === 'function') {
                mergedSx = buttonProps.sx({ isActive, index, theme });
              } else {
                mergedSx = { ...baseButtonSx, ...buttonProps.sx };
              }
            }
            
            return (
              <Button
                key={index}
                onClick={() => handleButtonClick(index)}
                sx={mergedSx}
                {...(buttonProps ? Object.fromEntries(Object.entries(buttonProps).filter(([key]) => key !== 'sx')) : {})}
              >
                {tab.label}
              </Button>
            );
          })}
        </Box>
      )}

      {/* Render Content */}
      {variant === 'buttons' && visibleTabs[activeTab]?.content}
      {variant === 'tabs' && (
        <Box sx={{ p: 0 }}>
          {visibleTabs[activeTab]?.content}
        </Box>
      )}
    </Box>
  );
};

export default CustomTabs;